import clsx from 'clsx';
import React, { useEffect } from 'react';
import './table.css';

import {
  IconSelector,
  IconChevronUp,
  IconChevronDown,
} from '@tabler/icons-react';

import { useFormatText } from '@/hooks/useFormatText';
import { useSkeletonStore } from '@/store/useSkeletonStore';

import Button from '../Button/Button';
import { Checkbox } from '../Checkbox';
import EmptyState from '../EmptyState';
import SkeletonLoader from '../SkeletonLoader';

const PRIMARY_ICON_CLASS = 'ms-2.5 text-primary-600';
const NOT_FOUND_IMG = '/not-found.svg';

export interface ICustomTableColumn<T = object> {
  title: string;
  dataIndex?: string;
  key: string;
  render?: (record: T, index: number) => React.ReactNode | null;
  width?: string;
  align?: 'start' | 'center' | 'end';
  sorter?: boolean;
  handleSort?: () => void;
}

interface ICustomTableProps<T extends object> {
  dataSource: T[];
  columns: ICustomTableColumn<T>[];
  rowKey?: string;
  total?: number;
  currentPage?: number;
  setCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
  onChangePagination?: (page: number) => void;
  className?: string;
  maxHeight?: string;
  hidePagination?: boolean;
  onPageSizeChange?: (pageSize: number) => void;
  onRowClick?: (id: string) => void;
  showHeader?: boolean;
  emptyImgSrc?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyButtonText?: string;
  emptyButtonIcon?: React.ReactNode;
  onEmptyBtnClick?: () => void;
  isEmptySearch?: boolean;
  isEmpty?: boolean;
  emptyClassName?: string;
  isLoading?: boolean;
  fieldName?: string;
  sortDirection?: string;
  lodingClassName?: string;
  rootClassName?: string;
  pageSize?: number;
  selectedRowKeys?: string[];
  onSelectionChange?: (keys: string[]) => void;
  dataTestId?: string;
}

const CustomTable = <T extends object>({
  dataSource,
  columns,
  rowKey = 'id',
  total,
  currentPage = 1,
  setCurrentPage,
  onChangePagination,
  className = '',
  hidePagination = false,
  // maxHeight = 'max-h-[41.844rem]',
  onRowClick,
  showHeader = true,
  emptyImgSrc,
  emptyTitle,
  emptyDescription,
  emptyButtonText,
  isEmpty = false,
  onEmptyBtnClick,
  emptyClassName,
  emptyButtonIcon,
  isLoading = false,
  isEmptySearch = false,
  fieldName,
  sortDirection,
  lodingClassName,
  rootClassName,
  pageSize = 10,
  selectedRowKeys = [],
  onSelectionChange,
  dataTestId = 'table',
}: ICustomTableProps<T>): React.ReactElement => {
  const { formatText } = useFormatText();
  const { isTableLoading } = useSkeletonStore();

  const showSkeleton = isTableLoading || isLoading;

  useEffect(() => {
    if (dataSource.length === 0 && currentPage > 1) {
      setCurrentPage?.((prevPage) => prevPage - 1);
    }
  }, [dataSource, currentPage, setCurrentPage]);

  useEffect(() => {
    if (onChangePagination) onChangePagination(currentPage);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  //on page change scroll to top
  useEffect(() => {
    if (hidePagination) return;
    const scrollableContainer = document.querySelector('.main-container');
    if (scrollableContainer) {
      scrollableContainer.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [currentPage, hidePagination]);

  const isAllSelected =
    dataSource.length > 0 &&
    dataSource.every((record) =>
      selectedRowKeys.includes(record[rowKey as keyof T] as string)
    );

  const handleSelectAll = (checked: boolean) => {
    const allKeys = dataSource.map(
      (record) => record[rowKey as keyof T] as string
    );

    const keysToUpdate = checked
      ? Array.from(new Set([...selectedRowKeys, ...allKeys]))
      : selectedRowKeys.filter((key) => !allKeys.includes(key));

    onSelectionChange?.(keysToUpdate);
  };

  const handleSelectRow = (id: string, isCurrentlySelected: boolean) => {
    const updatedKeys = isCurrentlySelected
      ? [...selectedRowKeys, id]
      : selectedRowKeys.filter((key) => key !== id);

    onSelectionChange?.(updatedKeys);
  };

  const renderTableBody = () => {
    if (showSkeleton) {
      return (
        <>
          {[...Array(10)].map((_, rowIndex) => (
            <tr
              key={rowIndex}
              className="animate-pulse"
              role="row"
              aria-label="Laden"
              data-testid="table-skeleton"
            >
              {onSelectionChange && (
                <td className="px-4 py-3 w-10">
                  <SkeletonLoader className="h-6 w-full" variant="rect" />
                </td>
              )}
              {columns.map((col, colIndex) => (
                <td
                  key={colIndex}
                  className="px-4 py-3 text-sm font-medium text-neutral-50 relative"
                  style={{
                    width: col?.width || '100%',
                  }}
                  role="cell"
                >
                  <SkeletonLoader className="h-6 w-full" variant="rect" />
                </td>
              ))}
            </tr>
          ))}
        </>
      );
    }

    if (dataSource?.length > 0) {
      return (
        <>
          {dataSource?.map((record: T, index: number) => {
            const id = record[rowKey as keyof T] as string;
            const isSelected = selectedRowKeys.includes(id);

            return (
              <tr
                key={`${id}-${index}`}
                data-testid={`${dataTestId}-row-${id}`}
                className={clsx(
                  'border-b border-neutral-200 bg-shades-white! hover:bg-gray-50',
                  (() => {
                    const isLastRow = hidePagination
                      ? dataSource.length - 1 === dataSource.indexOf(record)
                      : dataSource.length >= pageSize &&
                        index === dataSource.length - 1;

                    return isLastRow ? 'border-0!' : '';
                  })(),
                  onRowClick && 'cursor-pointer!',
                  isSelected && 'bg-brand-light-25!'
                )}
                onClick={() => onRowClick?.(id)}
              >
                {onSelectionChange && (
                  <td className="px-4 py-2.75 w-10">
                    <Checkbox
                      checked={isSelected}
                      onChange={(checked) => handleSelectRow(id, checked)}
                      id={`row-${id}`}
                    />
                  </td>
                )}
                {columns.map((col) => (
                  <td
                    key={col.key as string}
                    className="px-4 py-2.75 text-sm font-medium text-neutral-primary relative"
                    style={{
                      minWidth: col?.width,
                      textAlign: col?.align,
                    }}
                  >
                    {col.render
                      ? col.render(record, index)
                      : (record?.[col.key as keyof T] as React.ReactNode)}
                  </td>
                ))}
              </tr>
            );
          })}
        </>
      );
    }

    return (
      <tr>
        <td
          colSpan={onSelectionChange ? columns.length + 1 : columns.length}
          className="relative"
        >
          <div className="flex flex-col items-center justify-center py-10 bg-white h-full">
            <img
              src={
                isEmptySearch ? NOT_FOUND_IMG : (emptyImgSrc ?? NOT_FOUND_IMG)
              }
              alt={
                isEmptySearch
                  ? 'No results found'
                  : (emptyTitle ?? 'No data found')
              }
              className="w-42.75 h-42.75 mb-4"
            />

            <h3 className="text-2xl font-semibold text-neutral-primary mb-1 text-center">
              {isEmptySearch
                ? formatText('common.table.no-search-title')
                : (emptyTitle ?? 'No data found')}
            </h3>
            <p className="text-neutral-secondary mb-6 text-center max-w-150">
              {isEmptySearch
                ? formatText('common.table.no-search-subtitle')
                : emptyDescription}
            </p>
            {!isEmptySearch && emptyButtonText && (
              <Button
                icon={emptyButtonIcon}
                label={emptyButtonText}
                variant="primary"
                className="px-6 h-11 cursor-pointer"
                onClick={onEmptyBtnClick}
              />
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div
      data-testid={dataTestId}
      className={clsx('w-full', className, {
        'flex items-center justify-center h-full': isEmpty,
      })}
    >
      {!isEmpty && (
        <div className="relative">
          <div
            className={clsx(
              'flex flex-col border border-neutral-200 rounded-2xl overflow-hidden bg-white relative',
              (showSkeleton || dataSource?.length === 0) && lodingClassName,
              rootClassName
            )}
          >
            <div className="grow overflow-auto custom-scrollbar">
              <table className="w-full shadow-small text-sm text-start text-gray-500 border-collapse">
                {showHeader && (
                  <thead className="bg-neutral-100 sticky top-0 z-20">
                    <tr>
                      {onSelectionChange && (
                        <th className="px-4 py-3 bg-neutral-100 w-10 text-start rounded-tl-lg!">
                          <Checkbox
                            checked={isAllSelected}
                            onChange={handleSelectAll}
                            id="select-all"
                          />
                        </th>
                      )}
                      {columns.map((col, index) => (
                        <th
                          key={col.key as string}
                          data-testid={`${dataTestId}-column-${col.key}`}
                          className={clsx(
                            'px-4 py-3 font-medium text-sm text-neutral-secondary bg-neutral-100',
                            col.sorter ? 'cursor-pointer' : 'cursor-default',
                            index === 0 &&
                              !onSelectionChange &&
                              'rounded-tl-lg!',
                            columns.length - 1 === index && 'rounded-tr-lg!',
                            'whitespace-nowrap'
                          )}
                          style={{
                            minWidth: col?.width,
                            textAlign: col?.align,
                          }}
                        >
                          <div
                            className={clsx(
                              'flex items-center',
                              col.align && `justify-${col?.align}`
                            )}
                          >
                            <span onClick={col.handleSort}>
                              {formatText(col.title) || col.title}
                            </span>
                            <span onClick={col.handleSort}>
                              {(() => {
                                if (!col?.sorter) return null;
                                if (fieldName !== col?.key) {
                                  return (
                                    <IconSelector
                                      height={20}
                                      width={20}
                                      className="ms-2.5 text-neutral-600"
                                    />
                                  );
                                }
                                if (sortDirection === 'ASC') {
                                  return (
                                    <IconChevronUp
                                      size={16}
                                      className={PRIMARY_ICON_CLASS}
                                    />
                                  );
                                }
                                if (sortDirection === 'DESC') {
                                  return (
                                    <IconChevronDown
                                      size={16}
                                      className={PRIMARY_ICON_CLASS}
                                    />
                                  );
                                }
                                return (
                                  <IconSelector
                                    height={20}
                                    width={20}
                                    className="ms-2.5 text-neutral-600"
                                  />
                                );
                              })()}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody className="relative">{renderTableBody()}</tbody>
              </table>
            </div>

            {!hidePagination && (
              <div className="bg-white border-t border-neutral-200 px-4 py-3 flex justify-between items-center z-10">
                <p className="text-neutral-primary text-sm font-medium text-nowrap">
                  {formatText('common.table.showing')}{' '}
                  {total === 0 ? 0 : (currentPage - 1) * pageSize + 1} -{' '}
                  {Math.min(currentPage * pageSize, total ?? 0)}{' '}
                  {formatText('common.table.of')} {total}{' '}
                  {formatText('common.table.entries')}
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    label={formatText('common.table.previous')}
                    className="px-4 h-10 min-w-25"
                    onClick={() =>
                      setCurrentPage?.((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1 || showSkeleton}
                  />
                  <Button
                    variant="outline"
                    label={formatText('common.table.next')}
                    className="px-4 h-10 min-w-25"
                    onClick={() =>
                      setCurrentPage?.((prev) =>
                        Math.min(prev + 1, Math.ceil((total ?? 0) / pageSize))
                      )
                    }
                    disabled={
                      currentPage >= Math.ceil((total ?? 0) / pageSize) ||
                      showSkeleton
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {isEmpty && (
        <EmptyState
          imageSrc={emptyImgSrc || NOT_FOUND_IMG}
          title={emptyTitle || formatText('common.no-data-found')}
          description={emptyDescription || ''}
          className={clsx(emptyClassName)}
          actions={
            emptyButtonText && (
              <Button
                label={emptyButtonText}
                variant="primary"
                icon={emptyButtonIcon}
                className="h-12 px-4 cursor-pointer"
                onClick={onEmptyBtnClick}
              />
            )
          }
        />
      )}
    </div>
  );
};

export default CustomTable;
